import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogIn, LogOut, Package, Settings, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

function AccountMenu() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const authorized = user && (user.role === "super_admin" || user.permissions?.manage_shop);

  if (loading) return <div className="w-10 h-10 rounded-full border border-white/10" />;

  if (!user) {
    return (
      <Link to="/connexion" className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/10 text-xs font-bold uppercase tracking-wider hover:border-[#00F0FF]/60 hover:text-[#00F0FF] transition-colors">
        <LogIn size={14} /> Connexion
      </Link>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs font-bold hover:border-white/30 transition-colors"
      >
        <User size={14} /> {user.name?.split(" ")[0]} <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} aria-label="Fermer le menu" />
          <div className="absolute right-0 top-12 z-50 w-48 card-glass p-2 space-y-1">
            <Link to="/mes-commandes" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/80 hover:bg-white/5">
              <Package size={14} /> Mes commandes
            </Link>
            {authorized && (
              <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/80 hover:bg-white/5">
                <Settings size={14} /> Administration
              </Link>
            )}
            <button
              onClick={() => { setOpen(false); logout(); navigate("/"); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/80 hover:bg-white/5"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { itemCount } = useCart();

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#050505]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-4 gap-3">
          <Link to="/" className="font-display font-black text-lg tracking-tight shrink-0">
            LA <span className="text-[#FF007F]">FRIBUE</span> <span className="text-white/40 font-normal text-xs align-middle hidden sm:inline">Boutique</span>
          </Link>
          <div className="flex items-center gap-2">
            <AccountMenu />
            <Link to="/panier" className="relative w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:border-[#00F0FF]/60 hover:text-[#00F0FF] transition-colors shrink-0">
              <ShoppingBag size={18} />
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF007F] text-[10px] font-bold flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/5 mt-16">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/30">
          <span>© 2026 La Fribue Technologies - marque de l'association La Fribue</span>
          <div className="flex gap-4">
            <a href="https://lafribue.fr" className="hover:text-white transition">lafribue.fr</a>
            <a href="https://lafribue.fr/cgv" className="hover:text-white transition">CGV</a>
            <a href="https://lafribue.fr/confidentialite" className="hover:text-white transition">Confidentialité</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
